import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../utils/api'

interface FormErrors {
  email?: string
  password?: string
  general?: string
}

interface LoginFormProps {
  onSuccess?: () => void
}

export default function LoginForm({ onSuccess }: LoginFormProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const navigate = useNavigate()

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!email.trim()) {
      newErrors.email = 'L\'email est obligatoire'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Format email invalide'
    }

    if (!password) {
      newErrors.password = 'Le mot de passe est obligatoire'
    } else if (password.length < 6) {
      newErrors.password = 'Minimum 6 caractères'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setLoading(true)
    setErrors({})

    try {
      console.log('📤 Sending login request...')
      const response = await api.post('/api/login', {
        email: email.toLowerCase(),
        password
      })

      console.log('✅ Login response received:', response.data)

      // Valider que la réponse contient les données requises
      if (!response.data.token || !response.data.userId) {
        console.error('❌ Response missing required fields:', response.data)
        setErrors({ general: 'Réponse serveur incomplète' })
        setLoading(false)
        return
      }

      // Sauvegarde les données
      localStorage.setItem('token', response.data.token)
      localStorage.setItem('userId', response.data.userId)
      localStorage.setItem('userEmail', response.data.email || email.toLowerCase())
      
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true')
      }
      console.log('💾 Data saved to localStorage')

      // Redirection immédiate vers la page d'accueil, même si le profil n'est pas encore complété
      console.log('🚀 Login successful, redirecting to /home...')
      onSuccess?.()
      navigate('/home')

    } catch (err: any) {
      console.error('❌ Login error:', err)
      const errorMessage = err.response?.data?.error || err.message || 'Erreur lors de la connexion'
      setErrors({ general: errorMessage })
      console.log('Error set to state:', errorMessage)
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      {errors.general && (
        <div className="error-message">
          <span className="error-icon">!</span>
          {errors.general}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="login-email">Email professionnel</label>
        <input
          id="login-email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
            setErrors({ ...errors, email: undefined })
          }}
          placeholder="votre.email@entreprise.com"
          disabled={loading}
        />
        {errors.email && (
          <div className="error-message">
            <span className="error-icon">!</span>
            {errors.email}
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="login-password">Mot de passe</label>
        <input
          id="login-password"
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value)
            setErrors({ ...errors, password: undefined })
          }}
          placeholder="••••••••"
          disabled={loading}
        />
        {errors.password && (
          <div className="error-message">
            <span className="error-icon">!</span>
            {errors.password}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="checkbox-group">
          <input
            id="remember-me"
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            disabled={loading}
          />
          <label htmlFor="remember-me">Se souvenir de moi</label>
        </div>
        <div className="forgot-password">
          <a href="#forgot">Mot de passe oublié ?</a>
        </div>
      </div>

      <button
        type="submit"
        className="btn-primary"
        disabled={loading}
      >
        {loading ? (
          <>
            <span className="spinner"></span>
            Connexion en cours...
          </>
        ) : (
          'Se connecter'
        )}
      </button>
    </form>
  )
}
